import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '../stores/workspace.store';
import { useAuthStore } from '../stores/auth.store';
import { useUserTasks } from '../hooks/useUserTasks';
import { DateRangeFilter } from '../components/Agenda/DateRangeFilter';
import { UserFilter } from '../components/Agenda/UserFilter';
import { TypeFilter } from '../components/Agenda/TypeFilter';
import { StatusFilter } from '../components/Agenda/StatusFilter';
import { ClientFilter } from '../components/Agenda/ClientFilter';
import { ProcessFilter } from '../components/Agenda/ProcessFilter';
import { KanbanBoard } from '../components/Agenda/KanbanBoard';
import { CalendarView } from '../components/Agenda/CalendarView';
import { WeekView } from '../components/Agenda/WeekView';
import { exportAgendaToPDF } from '../components/Agenda/exportAgendaToPDF';
import { useWebSocket } from '../hooks/useWebSocket';
import { Breadcrumb } from '../components/Breadcrumb';
import { TaskFormModal } from '../components/TaskFormModal';
import { TaskType, TaskStatus, MemberDto, TaskDto } from '@prometeus/contracts';
import api from '../lib/api';

export const Route = createFileRoute('/w/$workspaceId/agenda')({
  component: AgendaPage,
});

const AGENDA_KANBAN_HIDE_CANCELED_STORAGE_KEY = 'prometeus.agenda.kanban.hideCanceledColumn';

function loadHideCanceledKanbanColumn(workspaceId: string): boolean {
  try {
    return window.localStorage.getItem(`${AGENDA_KANBAN_HIDE_CANCELED_STORAGE_KEY}:${workspaceId}`) === '1';
  } catch {
    return false;
  }
}

type ViewMode = 'kanban' | 'calendar' | 'week';

function AgendaPage() {
  const { workspaceId } = Route.useParams();
  const queryClient = useQueryClient();
  const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const currentUser = useAuthStore((s) => s.user);
  const hasInitializedUserFilter = useRef(false);
  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [includeNoDateTasks, setIncludeNoDateTasks] = useState(false);
  const [includeUnassignedTasks, setIncludeUnassignedTasks] = useState(false);
  const [taskNameFilter, setTaskNameFilter] = useState<string>('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<TaskType[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<TaskStatus[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  const [selectedProcessId, setSelectedProcessId] = useState<string>('');
  const [selectedProcessNumber, setSelectedProcessNumber] = useState<string>('');
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [hideCanceledKanbanColumn, setHideCanceledKanbanColumn] = useState(() =>
    loadHideCanceledKanbanColumn(workspaceId)
  );

  // Garantir que o workspace da URL está no store e no localStorage (evita "User is not a member" ao editar task)
  useEffect(() => {
    if (!workspaceId) return;
    localStorage.setItem('workspaceId', workspaceId);
    if (!currentWorkspace || currentWorkspace.id !== workspaceId) {
      setWorkspace({ id: workspaceId, name: '', slug: '' });
    }
  }, [workspaceId, currentWorkspace?.id, setWorkspace]);

  useEffect(() => {
    setHideCanceledKanbanColumn(loadHideCanceledKanbanColumn(workspaceId));
  }, [workspaceId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        `${AGENDA_KANBAN_HIDE_CANCELED_STORAGE_KEY}:${workspaceId}`,
        hideCanceledKanbanColumn ? '1' : '0'
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, [workspaceId, hideCanceledKanbanColumn]);

  // Initialize WebSocket for real-time updates
  useWebSocket();

  const { data: workspace } = useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
  });

  const { tasks, isLoading, hasMore, fetchNextPage, isFetchingNextPage } = useUserTasks({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    includeNoDate: includeNoDateTasks,
    selectedMemberIds,
    includeUnassigned: includeUnassignedTasks,
    types: selectedTypes.length > 0 ? selectedTypes : undefined,
    statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
  });

  const filteredTasks = useMemo((): TaskDto[] => {
    let result = tasks;
    if (selectedClientId) result = result.filter((t) => t.clientId === selectedClientId);
    if (selectedProcessId) result = result.filter((t) => t.processId === selectedProcessId);
    if (taskNameFilter.trim()) {
      const term = taskNameFilter.trim().toLowerCase();
      result = result.filter((t) => t.title.toLowerCase().includes(term));
    }
    return result;
  }, [tasks, selectedClientId, selectedProcessId, taskNameFilter]);

  // Get members for PDF export and for default user filter
  const { data: members } = useQuery<MemberDto[]>({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const response = await api.get('/members');
      return response.data.data;
    },
    enabled: !!workspaceId,
  });

  // Por padrão, filtro de usuário = usuário que está visualizando a agenda (só na primeira carga)
  useEffect(() => {
    if (hasInitializedUserFilter.current || !members?.length || !currentUser?.id) return;
    const currentMember = members.find((m: MemberDto) => m.userId === currentUser.id);
    if (currentMember?.id) {
      setSelectedMemberIds([currentMember.id]);
      hasInitializedUserFilter.current = true;
    }
  }, [members, currentUser?.id]);

  const [isExporting, setIsExporting] = useState(false);

  const handleDateFilterChange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    setIncludeNoDateTasks(false);
  };

  const handleClientFilterChange = (clientId: string, clientName: string) => {
    setSelectedClientId(clientId);
    setSelectedClientName(clientName);
    setSelectedProcessId('');
    setSelectedProcessNumber('');
  };

  const handleProcessFilterChange = (processId: string, processNumber: string) => {
    setSelectedProcessId(processId);
    setSelectedProcessNumber(processNumber);
  };

  const handleExportPDF = async () => {
    if (!members) return;
    
    setIsExporting(true);
    try {
      await exportAgendaToPDF(
        filteredTasks,
        {
          selectedMemberIds,
          selectedTypes,
          selectedStatuses,
          dateFrom,
          dateTo,
          includeNoDate: includeNoDateTasks,
          includeUnassigned: includeUnassignedTasks,
        },
        members
      );
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao exportar PDF. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-8 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Agenda' },
          ]}
        />

        <div
          className="bg-white dark:bg-gray-800 mt-6 flex flex-col"
          style={{
            borderRadius: 15,
            boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div
            className="flex flex-col gap-4 flex-shrink-0 p-5 pb-4 dark:border-gray-700"
            style={{ borderBottom: '1px solid rgba(6, 20, 27, 0.08)' }}
          >
            {/* Título com ícone + botão + no canto superior direito */}
            <div className="flex justify-between items-start mb-1">
              <div className="flex items-center gap-3">
                <svg width="24" height="24" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <path opacity="0.5" d="M31.1666 14.1667H2.83325V26.9167C2.83325 28.0438 3.28102 29.1249 4.07805 29.9219C4.87508 30.7189 5.95608 31.1667 7.08325 31.1667H26.9166C28.0438 31.1667 29.1248 30.7189 29.9218 29.9219C30.7188 29.1249 31.1666 28.0438 31.1666 26.9167V14.1667ZM9.91659 11.3333C9.54086 11.3333 9.18053 11.1841 8.91485 10.9184C8.64917 10.6527 8.49992 10.2924 8.49992 9.91668V4.25001C8.49992 3.87429 8.64917 3.51395 8.91485 3.24828C9.18053 2.9826 9.54086 2.83334 9.91659 2.83334C10.2923 2.83334 10.6526 2.9826 10.9183 3.24828C11.184 3.51395 11.3333 3.87429 11.3333 4.25001V9.91668C11.3333 10.2924 11.184 10.6527 10.9183 10.9184C10.6526 11.1841 10.2923 11.3333 9.91659 11.3333ZM24.0833 11.3333C23.7075 11.3333 23.3472 11.1841 23.0815 10.9184C22.8158 10.6527 22.6666 10.2924 22.6666 9.91668V4.25001C22.6666 3.87429 22.8158 3.51395 23.0815 3.24828C23.3472 2.9826 23.7075 2.83334 24.0833 2.83334C24.459 2.83334 24.8193 2.9826 25.085 3.24828C25.3507 3.51395 25.4999 3.87429 25.4999 4.25001V9.91668C25.4999 10.2924 25.3507 10.6527 25.085 10.9184C24.8193 11.1841 24.459 11.3333 24.0833 11.3333Z" fill="#06141B"/>
                  <path d="M26.9166 5.66666H25.4999V9.91666C25.4999 10.2924 25.3507 10.6527 25.085 10.9184C24.8193 11.1841 24.459 11.3333 24.0833 11.3333C23.7075 11.3333 23.3472 11.1841 23.0815 10.9184C22.8158 10.6527 22.6666 10.2924 22.6666 9.91666V5.66666H11.3333V9.91666C11.3333 10.2924 11.184 10.6527 10.9183 10.9184C10.6526 11.1841 10.2923 11.3333 9.91659 11.3333C9.54086 11.3333 9.18053 11.1841 8.91485 10.9184C8.64917 10.6527 8.49992 10.2924 8.49992 9.91666V5.66666H7.08325C5.95608 5.66666 4.87508 6.11442 4.07805 6.91145C3.28102 7.70848 2.83325 8.78949 2.83325 9.91666V14.1667H31.1666V9.91666C31.1666 8.78949 30.7188 7.70848 29.9218 6.91145C29.1248 6.11442 28.0438 5.66666 26.9166 5.66666Z" fill="#06141B"/>
                </svg>
                <div>
                  <h1
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '16px',
                      fontWeight: 400,
                      color: '#06141B',
                    }}
                  >
                    Agenda
                  </h1>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 400,
                      color: '#9BA8AB',
                    }}
                  >
                    Gerencie suas tasks e eventos em formato Kanban ou Calendário
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Exportar PDF */}
                <button
                  onClick={handleExportPDF}
                  disabled={isExporting || filteredTasks.length === 0}
                  className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '18px',
                    backgroundColor: '#06141B',
                    color: '#F5F6F5',
                    fontWeight: 400,
                    borderRadius: 10,
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                  title={isExporting ? 'Exportando...' : 'Exportar lista em PDF'}
                >
                  {isExporting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#F5F6F5] border-t-transparent" />
                  ) : (
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: 'currentColor' }}
                    >
                      <path
                        d="M11.2932 2.793L8.29319 5.793C8.20035 5.88591 8.12671 5.9962 8.07649 6.11757C8.02627 6.23893 8.00044 6.369 8.00049 6.50035C8.00053 6.6317 8.02645 6.76175 8.07676 6.88309C8.12707 7.00442 8.20078 7.11466 8.29369 7.2075C8.3866 7.30035 8.49689 7.37398 8.61826 7.4242C8.73962 7.47442 8.8697 7.50025 9.00104 7.5002C9.13239 7.50016 9.26245 7.47424 9.38378 7.42393C9.50511 7.37362 9.61535 7.29991 9.70819 7.207L11.0002 5.914V11.5C11.0002 11.7652 11.1055 12.0196 11.2931 12.2071C11.4806 12.3946 11.735 12.5 12.0002 12.5C12.2654 12.5 12.5198 12.3946 12.7073 12.2071C12.8948 12.0196 13.0002 11.7652 13.0002 11.5V5.914L14.2932 7.207C14.3854 7.30251 14.4958 7.37869 14.6178 7.4311C14.7398 7.48351 14.871 7.5111 15.0038 7.51225C15.1366 7.5134 15.2682 7.4881 15.3911 7.43782C15.514 7.38754 15.6257 7.31329 15.7196 7.21939C15.8135 7.1255 15.8877 7.01385 15.938 6.89095C15.9883 6.76806 16.0136 6.63638 16.0124 6.5036C16.0113 6.37082 15.9837 6.2396 15.9313 6.1176C15.8789 5.99559 15.8027 5.88525 15.7072 5.793L12.7072 2.793C12.6143 2.70002 12.504 2.62627 12.3826 2.57594C12.2612 2.52562 12.1311 2.49971 11.9997 2.49971C11.8683 2.49971 11.7381 2.52562 11.6168 2.57594C11.4954 2.62627 11.3851 2.70002 11.2922 2.793H11.2932Z"
                        fill="currentColor"
                      />
                      <path
                        opacity="0.5"
                        d="M13 11.5C13 11.7652 12.8946 12.0196 12.7071 12.2071C12.5196 12.3946 12.2652 12.5 12 12.5C11.7348 12.5 11.4804 12.3946 11.2929 12.2071C11.1054 12.0196 11 11.7652 11 11.5V7.5L5 7.5C4.20459 7.50079 3.44199 7.81712 2.87956 8.37956C2.31712 8.94199 2.00079 9.70459 2 10.5L2 18.5C2.00079 19.2954 2.31712 20.058 2.87956 20.6204C3.44199 21.1829 4.20459 21.4992 5 21.5L19 21.5C19.7954 21.4992 20.558 21.1829 21.1204 20.6204C21.6829 20.058 21.9992 19.2954 22 18.5V10.5C21.9992 9.70459 21.6829 8.94199 21.1204 8.37956C20.558 7.81712 19.7954 7.50079 19 7.5H13V11.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button>

                {/* Botão + (adicionar tarefa) - canto superior direito */}
                <button
                  type="button"
                  onClick={() => setShowAddTaskModal(true)}
                  className="w-8 h-8 flex items-center justify-center transition-colors hover:opacity-80 flex-shrink-0"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '18px',
                    backgroundColor: '#06141B',
                    color: '#F5F6F5',
                    fontWeight: 400,
                    borderRadius: 10,
                    boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                  }}
                  title="Adicionar tarefa"
                >
                  +
                </button>
              </div>
            </div>

            {/* Filters Row - identidade do cabeçalho do cliente */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <UserFilter
                selectedMemberIds={selectedMemberIds}
                onChange={setSelectedMemberIds}
                includeUnassigned={includeUnassignedTasks}
                onIncludeUnassignedChange={setIncludeUnassignedTasks}
              />
              <DateRangeFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                allowNoDateTasks
                includeNoDate={includeNoDateTasks}
                onIncludeNoDateChange={setIncludeNoDateTasks}
                onChange={handleDateFilterChange}
                onClear={handleClearFilter}
              />
              <TypeFilter
                selectedTypes={selectedTypes}
                onChange={setSelectedTypes}
              />
              <StatusFilter
                selectedStatuses={selectedStatuses}
                onChange={setSelectedStatuses}
              />
              <ClientFilter
                selectedClientId={selectedClientId}
                selectedClientName={selectedClientName}
                onChange={handleClientFilterChange}
              />
              <ProcessFilter
                clientId={selectedClientId}
                selectedProcessId={selectedProcessId}
                selectedProcessNumber={selectedProcessNumber}
                onChange={handleProcessFilterChange}
              />
            </div>

            {/* View Mode Tabs - padrão Poppins regular 14px */}
            <div
              className="mt-2 flex flex-wrap items-center gap-2 justify-between border-b border-gray-200"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setViewMode('kanban')}
                  className="px-4 py-2 transition-colors border-b-2"
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: viewMode === 'kanban' ? '#06141B' : '#9BA8AB',
                    borderBottomColor: viewMode === 'kanban' ? '#06141B' : 'transparent',
                  }}
                >
                  Kanban
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className="px-4 py-2 transition-colors border-b-2"
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: viewMode === 'calendar' ? '#06141B' : '#9BA8AB',
                    borderBottomColor: viewMode === 'calendar' ? '#06141B' : 'transparent',
                  }}
                >
                  Calendário
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className="px-4 py-2 transition-colors border-b-2"
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: viewMode === 'week' ? '#06141B' : '#9BA8AB',
                    borderBottomColor: viewMode === 'week' ? '#06141B' : 'transparent',
                  }}
                >
                  Semanal
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={taskNameFilter}
                  onChange={(e) => setTaskNameFilter(e.target.value)}
                  placeholder="Filtrar por nome da tarefa"
                  className="h-9 w-64 rounded-[10px] border border-gray-200 bg-white px-3 text-[12px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#06141B]"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                />
                {viewMode === 'kanban' && (
                  <button
                    type="button"
                    onClick={() => setHideCanceledKanbanColumn((v) => !v)}
                    className="mb-0.5 px-3 py-1.5 rounded-[10px] transition-colors hover:opacity-90 border text-[12px] font-normal"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      color: '#06141B',
                      borderColor: 'rgba(6, 20, 27, 0.15)',
                      backgroundColor: hideCanceledKanbanColumn ? '#E5E7EB' : '#FFFFFF',
                    }}
                    title={
                      hideCanceledKanbanColumn
                        ? 'Exibir novamente a coluna de tarefas canceladas'
                        : 'Ocultar a coluna de tarefas canceladas (preferência salva neste navegador)'
                    }
                  >
                    {hideCanceledKanbanColumn ? 'Mostrar canceladas' : 'Ocultar canceladas'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 min-h-0 p-5 pt-4 flex flex-col">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#485A6A' }}></div>
                <span
                  className="ml-3"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                  }}
                >
                  Carregando tasks...
                </span>
              </div>
            ) : filteredTasks.length === 0 ? (
              <div
                className="text-center py-12"
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '15px',
                  border: '1px solid #E2E8F0',
                }}
              >
                <svg
                  className="mx-auto h-12 w-12"
                  style={{ color: '#A0AEC0' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3
                  className="mt-2"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#2D3748',
                  }}
                >
                  Nenhuma task encontrada
                </h3>
                <p
                  className="mt-1"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                  }}
                >
                  {dateFrom || dateTo || includeNoDateTasks || includeUnassignedTasks
                    ? 'Tente ajustar os filtros ou limpar as opções de período e responsáveis.'
                    : 'Você não possui tasks atribuídas no momento.'}
                </p>
              </div>
            ) : viewMode === 'kanban' ? (
              <div className="flex-1 min-h-0">
                <KanbanBoard
                  tasks={filteredTasks}
                  hideCanceledColumn={hideCanceledKanbanColumn}
                  hasMore={hasMore}
                  onLoadMore={() => {
                    void fetchNextPage();
                  }}
                  isLoadingMore={isFetchingNextPage}
                />
              </div>
            ) : viewMode === 'calendar' ? (
              <CalendarView tasks={filteredTasks} />
            ) : (
              <WeekView tasks={filteredTasks} />
            )}
          </div>
        </div>
      </div>

      {showAddTaskModal && (
        <TaskFormModal
          processId={selectedProcessId || undefined}
          initialClientId={selectedClientId || undefined}
          initialClientName={selectedClientName || undefined}
          initialProcessId={selectedProcessId || undefined}
          initialProcessNumber={selectedProcessNumber || undefined}
          onClose={() => setShowAddTaskModal(false)}
          onSuccess={() => {
            setShowAddTaskModal(false);
            queryClient.invalidateQueries({ queryKey: ['user-tasks', workspaceId] });
            queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] });
          }}
        />
      )}
    </div>
  );
}

